import { browser } from '$app/environment';
import type {
  AcquisitionSimulationView,
  CreateAcquisitionSimulationDto,
  UpdateAcquisitionSimulationDto,
} from '$lib/utils/api-types';
import type { GeocodedResponse } from '$lib/utils/definitions';
import { getGeocodedResponseLabel } from '$lib/utils/helpers';
import type { Logement, PretLisse, Zone } from '$lib/utils/lissage-ptz';

type Step = {
  title: string;
  step: number;
};

class AcquisitionSimulator {
  public steps: Step[] = [
    {
      title: 'Votre futur logement',
      step: 1,
    },
    {
      title: 'Votre apport personnel',
      step: 2,
    },
    {
      title: "Frais annexes à l'acquisition",
      step: 3,
    },
    {
      title: "Synthèse de l'apport et des frais",
      step: 4,
    },
    {
      title: 'Prêt immobilier & prêt à taux zéro',
      step: 5,
    },
    {
      title: 'Lissage des prêts',
      step: 6,
    },
    {
      title: 'Redevance BRS & charges',
      step: 7,
    },
    {
      title: 'Récapitulatif de la simulation',
      step: 8,
    },
  ];
  public currentStep: Step = $state(this.steps[0]);
  public previousStep: Step | null = $derived.by(() => {
    if (this.currentStep.step < 2) {
      return null;
    } else {
      return this.steps[this.currentStep.step - 2];
    }
  });
  public nextStep: Step | null = $derived.by(() => {
    if (this.currentStep.step >= this.steps.length) {
      return null;
    } else {
      return this.steps[this.currentStep.step];
    }
  });

  public housingPrice: number | undefined = $state();
  public selectedLocation: GeocodedResponse['properties'] | undefined =
    $state();
  public autocompleteValue = $derived(
    getGeocodedResponseLabel(this.selectedLocation) || '',
  );
  public brsZone: Zone | undefined = $state();
  public surface: number | undefined = $state();
  public housingType: 'new' | 'old' | undefined = $state();

  public ownContribution: number | undefined = $state();

  public notaryFees: number | undefined = $state();
  public loanFees: number | undefined = $state();
  public oneTimeExpenses: number | undefined = $state();

  public interestRate: number | undefined = $state();
  public loanDuration: number | undefined = $state();
  public inHousePeopleAmount: number | undefined = $state();
  public fiscalIncome: number | undefined = $state();
  public ptzType: Logement | undefined = $state();
  public pretLisse: PretLisse | undefined = $state();

  public brsFees: number | undefined = $state(1);
  public yearlyPropertyTax: number | undefined = $state(1000);
  public yearlyHouseingInsurance: number | undefined = $state(300);
  public condominiumFeesFrequency:
    | 'yearly'
    | 'monthly'
    | 'trimestrial'
    | undefined = $state('monthly');
  public condominiumFees: number | undefined = $state(150);
  public monthlyExpenses: number | undefined = $state(100);

  public loading = $state(false);
  public acquisitionSimulation: AcquisitionSimulationView | null = $state(null);

  public estimatedNotaryFees: number = $derived.by(() => {
    if (this.housingPrice) {
      if (this.housingType === 'new') {
        return Math.round(this.housingPrice * 0.023);
      } else {
        return Math.round(this.housingPrice * 0.078);
      }
    }

    return 0;
  });

  public totalFees: number = $derived(
    (this.notaryFees || this.estimatedNotaryFees) + (this.oneTimeExpenses || 0),
  );

  public totalCost: number = $derived(
    (this.housingPrice || 0) + (this.totalFees || 0),
  );

  public loanAmount: number = $derived(
    Math.max(
      (this.housingPrice || 0) +
        (this.totalFees || 0) -
        (this.ownContribution || 0),
      0,
    ),
  );

  public estimatedLoanFees: number = $derived.by(() => {
    if (!this.housingPrice) return 0;

    return Math.round(500 + this.loanAmount * 0.008);
  });

  public monthlyBrsFees = $derived(
    this.brsFees ? (this.brsFees as number) * (this.surface as number) : 0,
  );

  public yearlyBrsFees = $derived(
    this.brsFees ? (this.brsFees as number) * (this.surface as number) * 12 : 0,
  );

  public monthlyPropertyTax = $derived(
    this.yearlyPropertyTax ? (this.yearlyPropertyTax as number) / 12 : 0,
  );

  public monthlyHouseingInsurance = $derived(
    this.yearlyHouseingInsurance
      ? (this.yearlyHouseingInsurance as number) / 12
      : 0,
  );

  public monthlyCondominiumFees = $derived.by(() => {
    if (this.condominiumFees && this.condominiumFees > 0) {
      if (this.condominiumFeesFrequency === 'monthly') {
        return this.condominiumFees as number;
      } else if (this.condominiumFeesFrequency === 'trimestrial') {
        return (this.condominiumFees as number) / 3;
      } else if (this.condominiumFeesFrequency === 'yearly') {
        return (this.condominiumFees as number) / 12;
      }
    }

    return 0;
  });

  public yearlyCondominiumFees = $derived.by(() => {
    if (this.condominiumFees && this.condominiumFees > 0) {
      if (this.condominiumFeesFrequency === 'monthly') {
        return (this.condominiumFees as number) * 12;
      } else if (this.condominiumFeesFrequency === 'trimestrial') {
        return (this.condominiumFees as number) * 3;
      } else if (this.condominiumFeesFrequency === 'yearly') {
        return this.condominiumFees as number;
      }
    }

    return 0;
  });

  public yearlyExpenses = $derived(
    this.monthlyExpenses ? (this.monthlyExpenses as number) * 12 : 0,
  );

  public goToPreviousStep = () => {
    if (this.previousStep) {
      this.currentStep = this.previousStep;
      this.resetScroll();
    }
  };

  public goToNextStep = () => {
    if (this.nextStep) {
      this.currentStep = this.nextStep;
      this.resetScroll();
    }
  };

  private resetScroll = () => {
    if (browser) {
      document.getElementById('simulateur')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  public createAcquisitionSimulation = async (
    payload: CreateAcquisitionSimulationDto,
  ) => {
    this.loading = true;

    const response = await fetch('/api/acquisition-simulations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!data.message) {
      this.acquisitionSimulation = data;
    }

    this.loading = false;
    this.goToNextStep();
  };

  public updateAcquisitionSimulation = async (
    payload: UpdateAcquisitionSimulationDto,
  ) => {
    this.loading = true;

    const response = await fetch(
      `/api/acquisition-simulations/${this.acquisitionSimulation?.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    const data = await response.json();

    if (!data.message) {
      this.acquisitionSimulation = data;
    }

    this.loading = false;
    this.goToNextStep();
  };
}

const acquisitionSimulatorManager = new AcquisitionSimulator();
export default acquisitionSimulatorManager;
